// Client-side helpers for fetching broker_transaction CSVs directly from Azure Blob
// Storage (trading-data-v2), using a SAS token issued by the backend gate endpoint
// (GET /api/broker/transaction/sas-token). Mirrors the direct-blob pattern used by
// OrderbookReplay/duckdbService.ts and the broker_transaction_bundle ZIP layout
// produced by 1e_brokertransaction.rs, ported from backend/src/routes/broker.ts.

import { blobCacheKey } from './cacheKey';
import { fetchZipEntry } from './zipRangeReader';

export type BrokerTransactionPivot = 'Broker' | 'Stock';

export interface BrokerTransactionData {
  Emiten: string;
  BuyerVol: number;
  BuyerValue: number;
  SellerVol: number;
  SellerValue: number;
  NetBuyVol: number;
  NetBuyValue: number;
  BuyerAvg: number;
  SellerAvg: number;
  TotalVolume: number;
  AvgPrice: number;
  TransactionCount: number;
  TotalValue: number;
  // VALUE Table: Buyer and Seller data (from CSV columns 0-15)
  BCode?: string;      // Buyer Code (from CSV column 0)
  BLot?: number;       // Buyer Lot (from CSV column 1)
  BFreq?: number;      // Buyer Frequency (from CSV column 4)
  BLotPerFreq?: number; // Buyer Lot/Frequency (Lot/F from CSV column 5)
  BOrdNum?: number;    // Buyer Order Number (from CSV column 6)
  NewBuyerOrdNum?: number; // New Buyer Order Number (for BOR display)
  BLotPerOrdNum?: number; // Buyer Lot/Order Number (Lot/ON from CSV column 7)
  SCode?: string;      // Seller Code (from CSV column 8)
  SLot?: number;       // Seller Lot (from CSV column 9)
  SFreq?: number;      // Seller Frequency (from CSV column 12)
  SLotPerFreq?: number; // Seller Lot/Frequency (Lot/F from CSV column 13)
  SOrdNum?: number;    // Seller Order Number (from CSV column 14)
  NewSellerOrdNum?: number; // New Seller Order Number (for SOR display)
  SLotPerOrdNum?: number; // Seller Lot/Order Number (Lot/ON from CSV column 15)
  // NET Table: Net Buy and Net Sell data (from CSV columns 16-31)
  NBCode?: string;     // Net Buy Code (from CSV column 16)
  NBLot?: number;      // Net Buy Lot (from CSV column 17)
  NBVal?: number;      // Net Buy Value (from CSV column 18)
  NBAvg?: number;      // Net Buy Average (from CSV column 19)
  NBFreq?: number;     // Net Buy Frequency (from CSV column 20)
  NBLotPerFreq?: number; // Net Buy Lot/Frequency (from CSV column 21)
  NBOrdNum?: number;   // Net Buy Order Number (from CSV column 22)
  NBLotPerOrdNum?: number; // Net Buy Lot/Order Number (NLot/ON) from CSV column 23
  NSCode?: string;     // Net Sell Code (from CSV column 24)
  NSLot?: number;      // Net Sell Lot (from CSV column 25)
  NSVal?: number;      // Net Sell Value (from CSV column 26)
  NSAvg?: number;      // Net Sell Average (from CSV column 27)
  NSFreq?: number;     // Net Sell Frequency (from CSV column 28)
  NSLotPerFreq?: number; // Net Sell Lot/Frequency (from CSV column 29)
  NSOrdNum?: number;   // Net Sell Order Number (from CSV column 30)
  NSLotPerOrdNum?: number; // Net Sell Lot/Order Number (NLot/ON) from CSV column 31
  BNoSplit?: number;     // Buyer NoSplit groups (distinct TRX_TIME per broker)
  BLotPerNoSplit?: number; // Buyer Lot per NoSplit group (Lot/NoSplit_Buy)
  SNoSplit?: number;     // Seller NoSplit groups (distinct TRX_TIME per broker)
  SLotPerNoSplit?: number; // Seller Lot per NoSplit group (Lot/NoSplit_Sell)
  NBNoSplit?: number;    // NetBuy NoSplit (NoSplit_Buy - NoSplit_Sell)
  NBLotPerNoSplit?: number; // NetBuy Lot per NoSplit (Lot/NoSplit_NetBuy)
  NSNoSplit?: number;    // NetSell NoSplit (NoSplit_Sell - NoSplit_Buy)
  NSLotPerNoSplit?: number; // NetSell Lot per NoSplit (Lot/NoSplit_NetSell)
  // Custom fields
  stock?: string;
  // Calculated fields
  NetBuyAvg?: number;  // Calculated Net Buy Average
  NetSellVol?: number; // Calculated Net Sell Volume
  NetSellValue?: number; // Calculated Net Sell Value
  NetSellAvg?: number; // Calculated Net Sell Average
}

const BT_ROOT = 'broker_transaction/';

/**
 * Build the legacy per-file broker_transaction blob path under trading-data-v2,
 * mirroring getAzurePath() in backend/src/routes/broker.ts. Used as a fallback
 * for dates predating the broker_transaction_bundle ZIPs.
 */
function getLegacyBrokerTransactionPath(
  code: string,
  normalizedDateStr: string,
  pivot: BrokerTransactionPivot,
  hasBoardFilter: boolean,
  hasInvFilter: boolean,
  normalizedBoardFilter: string,
  normalizedInvFilter: string,
): string {
  const folderMap: { [key: string]: string } = { RG: 'rg', TN: 'tn', NG: 'ng' };

  let folderPrefix: string;
  let filePrefix: string;

  if (pivot === 'Stock') {
    if (hasBoardFilter) {
      const board = normalizedBoardFilter.toUpperCase();
      const folderType = folderMap[board] || board.toLowerCase();
      const parts = [folderType];
      if (hasInvFilter) parts.push(normalizedInvFilter.toLowerCase());
      folderPrefix = `broker_transaction_stock_${parts.join('_')}`;
      filePrefix = folderPrefix;
    } else if (hasInvFilter) {
      folderPrefix = 'broker_transaction_stock';
      filePrefix = `broker_transaction_stock_${normalizedInvFilter.toLowerCase()}`;
    } else {
      folderPrefix = 'broker_transaction_stock';
      filePrefix = 'broker_transaction_stock';
    }
  } else {
    if (hasBoardFilter) {
      const board = normalizedBoardFilter.toUpperCase();
      const folderType = folderMap[board] || board.toLowerCase();
      const parts = [folderType];
      if (hasInvFilter) parts.push(normalizedInvFilter.toLowerCase());
      folderPrefix = `broker_transaction_${parts.join('_')}`;
      filePrefix = folderPrefix;
    } else if (hasInvFilter) {
      folderPrefix = 'broker_transaction';
      filePrefix = `broker_transaction_${normalizedInvFilter.toLowerCase()}`;
    } else {
      folderPrefix = 'broker_transaction';
      filePrefix = 'broker_transaction';
    }
  }

  return `${BT_ROOT}${folderPrefix}/${filePrefix}_${normalizedDateStr}/${code}.csv`;
}

export interface BrokerTransactionBlobPaths {
  /** broker_transaction_bundle/{YYYYMMDD}/{broker|stock}/{code}.zip — preferred source. */
  bundlePath: string;
  /** Entry name inside `bundlePath` for this investor/board filter combination. */
  entryName: string;
  /** Legacy per-file CSV path, used as a fallback for dates predating bundling. */
  legacyPath: string;
}

/**
 * Build the broker_transaction bundle/entry/legacy paths under trading-data-v2,
 * mirroring the new 1e_brokertransaction.rs bundle layout:
 * - broker_transaction_bundle/{date}/broker/{code}.zip, entry broker{dsuf}.csv
 * - broker_transaction_bundle/{date}/stock/{code}.zip,  entry stock{dsuf}.csv
 *
 * `dsuf` is derived from (invFilter, boardFilter) using the same board-then-investor
 * suffix scheme as the 12 COMBOS in 1e_brokertransaction.rs.
 */
export function getBrokerTransactionBlobPath(
  code: string,
  dateStr: string,
  pivot: BrokerTransactionPivot = 'Broker',
  invFilter?: string,
  boardFilter?: string,
): BrokerTransactionBlobPaths {
  // Azure blob folder/file names use YYYYMMDD (no dashes)
  const normalizedDateStr = dateStr.includes('-') ? dateStr.replace(/-/g, '') : dateStr;

  const normalizedBoardFilter = boardFilter ? boardFilter.trim() : '';
  const hasBoardFilter = normalizedBoardFilter !== '' && normalizedBoardFilter !== 'All Trade';

  const normalizedInvFilter = invFilter ? invFilter.trim().toUpperCase() : '';
  const hasInvFilter = normalizedInvFilter === 'F' || normalizedInvFilter === 'D';

  const folderMap: { [key: string]: string } = { RG: 'rg', TN: 'tn', NG: 'ng' };
  const boardSuf = hasBoardFilter
    ? `_${folderMap[normalizedBoardFilter.toUpperCase()] || normalizedBoardFilter.toLowerCase()}`
    : '';
  const invSuf = hasInvFilter ? `_${normalizedInvFilter.toLowerCase()}` : '';
  const dsuf = `${boardSuf}${invSuf}`;

  const scope = pivot === 'Stock' ? 'stock' : 'broker';

  return {
    bundlePath: `broker_transaction_bundle/${normalizedDateStr}/${scope}/${code}.zip`,
    entryName: `${scope}${dsuf}.csv`,
    legacyPath: getLegacyBrokerTransactionPath(
      code, normalizedDateStr, pivot, hasBoardFilter, hasInvFilter, normalizedBoardFilter, normalizedInvFilter,
    ),
  };
}

/**
 * Parse broker_transaction CSV data (Azure comma-delimited format with header-based
 * column lookup, or legacy semicolon-delimited 32-column format), mirroring
 * parseBrokerTransactionCSV() in backend/src/routes/broker.ts.
 */
export function parseBrokerTransactionCSV(csvData: string, pivot: BrokerTransactionPivot = 'Broker'): BrokerTransactionData[] {
  const lines = csvData.trim().split('\n');
  if (lines.length < 2) {
    return [];
  }

  const transactionData: BrokerTransactionData[] = [];

  // Detect format from header line
  let isAzureFormat = false;
  let isStockFormat = false;
  let azureHeader: string[] = [];
  const headerLine = lines[0]?.trim() || '';
  azureHeader = headerLine.split(',').map(v => v.trim());
  // Azure format for Broker pivot: Emiten,BuyerVol,BuyerValue,... (first column "Emiten")
  // Azure format for Stock pivot: Broker,BuyerVol,BuyerValue,... (first column "Broker")
  isStockFormat = azureHeader.length >= 20 && azureHeader[0] === 'Broker';
  if (!isStockFormat && pivot === 'Stock' && azureHeader.length >= 20) {
    isStockFormat = true;
  }
  isAzureFormat = (azureHeader.length >= 20 && azureHeader[0] === 'Emiten') || isStockFormat;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]?.trim();
    if (!line) {
      continue;
    }

    let values: string[];
    if (isAzureFormat) {
      values = line.split(',').map(v => v.trim());
      if (i === 1 && (values[0] === 'Emiten' || values[0] === 'Broker')) {
        continue;
      }
    } else {
      values = line.split(';').map(v => v.trim());
    }

    let emiten: string;
    let bCode: string, sCode: string, nbCode: string, nsCode: string;
    let bLot: number, sLot: number, nbLot: number, nsLot: number;
    let bVal: number, sVal: number, nbVal: number, nsVal: number;
    let bAvg: number, sAvg: number, nbAvg: number, nsAvg: number;
    let bFreq: number, sFreq: number, nbFreq: number, nsFreq: number;
    let bOrdNum: number, sOrdNum: number, nbOrdNum: number, nsOrdNum: number;
    let newBuyerOrdNum: number, newSellerOrdNum: number;
    let bLotPerFreq: number, sLotPerFreq: number, nbLotPerFreq: number, nsLotPerFreq: number;
    let bLotPerOrdNum: number, sLotPerOrdNum: number, nbLotPerOrdNum: number, nsLotPerOrdNum: number;
    let bNoSplit: number = 0, sNoSplit: number = 0;
    let bLotPerNoSplit: number = 0, sLotPerNoSplit: number = 0;
    let nbNoSplit: number = 0, nsNoSplit: number = 0;
    let nbLotPerNoSplit: number = 0, nsLotPerNoSplit: number = 0;
    // For Azure Stock format: volumes/values are read directly from the CSV columns below
    let buyerVol: number = 0, sellerVol: number = 0, netBuyVol: number = 0, netBuyValue: number = 0, netSellVol: number = 0, netSellValue: number = 0;

    if (isAzureFormat) {
      const getColumnIndex = (colName: string): number => {
        return azureHeader.findIndex(col => col.trim().toLowerCase() === colName.toLowerCase());
      };

      let firstColIdx = isStockFormat ? getColumnIndex('Broker') : getColumnIndex('Emiten');
      if (firstColIdx === -1) {
        firstColIdx = getColumnIndex('Emiten') !== -1 ? getColumnIndex('Emiten') : 0;
      }
      const emitenIdx = firstColIdx;
      const buyerVolIdx = getColumnIndex('BuyerVol');
      const buyerValueIdx = getColumnIndex('BuyerValue');
      const buyerAvgIdx = getColumnIndex('BuyerAvg');
      const buyerFreqIdx = getColumnIndex('BuyerFreq');
      // BLotPerFreq/BLotPerOrdNum (and the Net*/Seller equivalents below) use the
      // same column names in both BT_BROKER_HEADER and BT_STOCK_HEADER, so the
      // lookup doesn't depend on isStockFormat.
      const buyerLotPerFreqIdx = getColumnIndex('BLotPerFreq');
      const buyerOrdNumIdx = getColumnIndex('BuyerOrdNum');
      const newBuyerOrdNumIdx = getColumnIndex('NewBuyerOrdNum');
      const buyerLotPerOrdNumIdx = getColumnIndex('BLotPerOrdNum');
      const bLotIdx = isStockFormat ? getColumnIndex('BLot') : -1;
      const sellerVolIdx = getColumnIndex('SellerVol');
      const sellerValueIdx = getColumnIndex('SellerValue');
      const sellerAvgIdx = getColumnIndex('SellerAvg');
      const sellerFreqIdx = getColumnIndex('SellerFreq');
      const sellerLotPerFreqIdx = getColumnIndex('SLotPerFreq');
      const sellerOrdNumIdx = getColumnIndex('SellerOrdNum');
      const newSellerOrdNumIdx = getColumnIndex('NewSellerOrdNum');
      const sellerLotPerOrdNumIdx = getColumnIndex('SLotPerOrdNum');
      const sLotIdx = isStockFormat ? getColumnIndex('SLot') : -1;
      const netBuyVolIdx = getColumnIndex('NetBuyVol');
      const netBuyValueIdx = getColumnIndex('NetBuyValue');
      const netBuyAvgIdx = getColumnIndex('NetBuyAvg');
      const netBuyFreqIdx = getColumnIndex('NetBuyFreq');
      const netBuyLotPerFreqIdx = getColumnIndex('NBLotPerFreq');
      const netBuyOrdNumIdx = getColumnIndex('NetBuyOrdNum');
      const netBuyLotPerOrdNumIdx = getColumnIndex('NBLotPerOrdNum');
      const nbLotIdx = isStockFormat ? getColumnIndex('NBLot') : -1;
      const netSellVolIdx = getColumnIndex('NetSellVol');
      const netSellValueIdx = getColumnIndex('NetSellValue');
      const netSellAvgIdx = getColumnIndex('NetSellAvg');
      const netSellFreqIdx = getColumnIndex('NetSellFreq');
      const netSellLotPerFreqIdx = getColumnIndex('NSLotPerFreq');
      const netSellOrdNumIdx = getColumnIndex('NetSellOrdNum');
      const netSellLotPerOrdNumIdx = getColumnIndex('NSLotPerOrdNum');
      const nsLotIdx = isStockFormat ? getColumnIndex('NSLot') : -1;
      const bNoSplitIdx = getColumnIndex('NoSplit_Buy');
      const sNoSplitIdx = getColumnIndex('NoSplit_Sell');
      const bLotPerNoSplitIdx = getColumnIndex('Lot/NoSplit_Buy');
      const sLotPerNoSplitIdx = getColumnIndex('Lot/NoSplit_Sell');
      const nbNoSplitIdx = getColumnIndex('NoSplit_NetBuy');
      const nsNoSplitIdx = getColumnIndex('NoSplit_NetSell');
      const nbLotPerNoSplitIdx = getColumnIndex('Lot/NoSplit_NetBuy');
      const nsLotPerNoSplitIdx = getColumnIndex('Lot/NoSplit_NetSell');

      if (values.length < Math.max(emitenIdx, buyerVolIdx, sellerVolIdx, netBuyVolIdx, netSellVolIdx) + 1) {
        continue;
      }

      // Map to expected format - read directly from CSV
      emiten = values[emitenIdx] || '';
      buyerVol = parseFloat(values[buyerVolIdx] || '0') || 0;
      const buyerValue = parseFloat(values[buyerValueIdx] || '0') || 0;
      const buyerAvg = parseFloat(values[buyerAvgIdx] || '0') || 0;
      const buyerFreq = parseFloat(values[buyerFreqIdx] || '0') || 0;
      bLotPerFreq = buyerLotPerFreqIdx >= 0 ? (values[buyerLotPerFreqIdx] ? parseFloat(values[buyerLotPerFreqIdx]) : 0) : 0;
      const buyerOrdNum = parseFloat(values[buyerOrdNumIdx] || '0') || 0;
      newBuyerOrdNum = newBuyerOrdNumIdx >= 0 ? (parseFloat(values[newBuyerOrdNumIdx] || '0') || 0) : buyerOrdNum;
      bLotPerOrdNum = buyerLotPerOrdNumIdx >= 0 ? (values[buyerLotPerOrdNumIdx] ? parseFloat(values[buyerLotPerOrdNumIdx]) : 0) : 0;

      sellerVol = parseFloat(values[sellerVolIdx] || '0') || 0;
      const sellerValue = parseFloat(values[sellerValueIdx] || '0') || 0;
      const sellerAvg = parseFloat(values[sellerAvgIdx] || '0') || 0;
      const sellerFreq = parseFloat(values[sellerFreqIdx] || '0') || 0;
      sLotPerFreq = sellerLotPerFreqIdx >= 0 ? (values[sellerLotPerFreqIdx] ? parseFloat(values[sellerLotPerFreqIdx]) : 0) : 0;
      const sellerOrdNum = parseFloat(values[sellerOrdNumIdx] || '0') || 0;
      newSellerOrdNum = newSellerOrdNumIdx >= 0 ? (parseFloat(values[newSellerOrdNumIdx] || '0') || 0) : sellerOrdNum;
      sLotPerOrdNum = sellerLotPerOrdNumIdx >= 0 ? (values[sellerLotPerOrdNumIdx] ? parseFloat(values[sellerLotPerOrdNumIdx]) : 0) : 0;

      netBuyVol = parseFloat(values[netBuyVolIdx] || '0') || 0;
      netBuyValue = parseFloat(values[netBuyValueIdx] || '0') || 0;
      const netBuyAvg = parseFloat(values[netBuyAvgIdx] || '0') || 0;
      const netBuyFreq = parseFloat(values[netBuyFreqIdx] || '0') || 0;
      nbLotPerFreq = netBuyLotPerFreqIdx >= 0 ? (values[netBuyLotPerFreqIdx] ? parseFloat(values[netBuyLotPerFreqIdx]) : 0) : 0;
      const netBuyOrdNum = parseFloat(values[netBuyOrdNumIdx] || '0') || 0;
      nbLotPerOrdNum = netBuyLotPerOrdNumIdx >= 0 ? (values[netBuyLotPerOrdNumIdx] ? parseFloat(values[netBuyLotPerOrdNumIdx]) : 0) : 0;

      netSellVol = parseFloat(values[netSellVolIdx] || '0') || 0;
      netSellValue = parseFloat(values[netSellValueIdx] || '0') || 0;
      const netSellAvg = parseFloat(values[netSellAvgIdx] || '0') || 0;
      const netSellFreq = parseFloat(values[netSellFreqIdx] || '0') || 0;
      nsLotPerFreq = netSellLotPerFreqIdx >= 0 ? (values[netSellLotPerFreqIdx] ? parseFloat(values[netSellLotPerFreqIdx]) : 0) : 0;
      const netSellOrdNum = parseFloat(values[netSellOrdNumIdx] || '0') || 0;
      nsLotPerOrdNum = netSellLotPerOrdNumIdx >= 0 ? (values[netSellLotPerOrdNumIdx] ? parseFloat(values[netSellLotPerOrdNumIdx]) : 0) : 0;

      // NoSplit columns (absent on pre-regen dates → 0, UI hides them as '-')
      bNoSplit = bNoSplitIdx >= 0 ? (parseFloat(values[bNoSplitIdx] || '0') || 0) : 0;
      sNoSplit = sNoSplitIdx >= 0 ? (parseFloat(values[sNoSplitIdx] || '0') || 0) : 0;
      bLotPerNoSplit = bLotPerNoSplitIdx >= 0 ? (values[bLotPerNoSplitIdx] ? parseFloat(values[bLotPerNoSplitIdx]) : 0) : 0;
      sLotPerNoSplit = sLotPerNoSplitIdx >= 0 ? (values[sLotPerNoSplitIdx] ? parseFloat(values[sLotPerNoSplitIdx]) : 0) : 0;
      // NoSplit net columns (absent pre-regen → derive from gross like backend does)
      nbNoSplit = nbNoSplitIdx >= 0 ? (parseFloat(values[nbNoSplitIdx] || '0') || 0) : 0;
      nsNoSplit = nsNoSplitIdx >= 0 ? (parseFloat(values[nsNoSplitIdx] || '0') || 0) : 0;
      nbLotPerNoSplit = nbLotPerNoSplitIdx >= 0 ? (values[nbLotPerNoSplitIdx] ? parseFloat(values[nbLotPerNoSplitIdx]) : 0) : 0;
      nsLotPerNoSplit = nsLotPerNoSplitIdx >= 0 ? (values[nsLotPerNoSplitIdx] ? parseFloat(values[nsLotPerNoSplitIdx]) : 0) : 0;

      // For Stock format, lots are already in the CSV (BLot, SLot, NBLot, NSLot)
      // For Broker format, convert volumes to lots (1 lot = 100 shares)
      if (isStockFormat) {
        bLot = bLotIdx >= 0 ? (parseFloat(values[bLotIdx] || '0') || 0) : (buyerVol / 100);
        sLot = sLotIdx >= 0 ? (parseFloat(values[sLotIdx] || '0') || 0) : (sellerVol / 100);
        nbLot = nbLotIdx >= 0 ? (parseFloat(values[nbLotIdx] || '0') || 0) : (netBuyVol / 100);
        nsLot = nsLotIdx >= 0 ? (parseFloat(values[nsLotIdx] || '0') || 0) : (netSellVol / 100);
      } else {
        bLot = buyerVol / 100;
        sLot = sellerVol / 100;
        nbLot = netBuyVol / 100;
        nsLot = netSellVol / 100;
      }

      // If Lot/F and Lot/ON are not in CSV, calculate them as fallback
      if (buyerLotPerFreqIdx < 0) {
        bLotPerFreq = buyerFreq !== 0 ? bLot / buyerFreq : 0;
      }
      if (buyerLotPerOrdNumIdx < 0) {
        bLotPerOrdNum = buyerOrdNum !== 0 ? bLot / buyerOrdNum : 0;
      }
      if (sellerLotPerFreqIdx < 0) {
        sLotPerFreq = sellerFreq !== 0 ? sLot / sellerFreq : 0;
      }
      if (sellerLotPerOrdNumIdx < 0) {
        sLotPerOrdNum = sellerOrdNum !== 0 ? sLot / sellerOrdNum : 0;
      }
      if (netBuyLotPerFreqIdx < 0) {
        nbLotPerFreq = netBuyFreq !== 0 ? nbLot / netBuyFreq : 0;
      }
      if (netBuyLotPerOrdNumIdx < 0) {
        nbLotPerOrdNum = netBuyOrdNum !== 0 ? nbLot / netBuyOrdNum : 0;
      }
      if (netSellLotPerFreqIdx < 0) {
        nsLotPerFreq = netSellFreq !== 0 ? nsLot / netSellFreq : 0;
      }
      if (netSellLotPerOrdNumIdx < 0) {
        nsLotPerOrdNum = netSellOrdNum !== 0 ? nsLot / netSellOrdNum : 0;
      }
      // NoSplit Lot/NS fallback for pre-regen CSVs (recompute from lots)
      if (bLotPerNoSplitIdx < 0) {
        bLotPerNoSplit = bNoSplit !== 0 ? bLot / bNoSplit : 0;
      }
      if (sLotPerNoSplitIdx < 0) {
        sLotPerNoSplit = sNoSplit !== 0 ? sLot / sNoSplit : 0;
      }
      // NoSplit net fallback for pre-regen CSVs (derive like backend: signed differences)
      if (nbNoSplitIdx < 0) {
        nbNoSplit = bNoSplit - sNoSplit;
        nsNoSplit = sNoSplit - bNoSplit;
      }
      if (nbLotPerNoSplitIdx < 0) {
        nbLotPerNoSplit = nbNoSplit !== 0 && nbLot !== 0 ? nbLot / nbNoSplit : 0;
      }
      if (nsLotPerNoSplitIdx < 0) {
        nsLotPerNoSplit = nsNoSplit !== 0 && nsLot !== 0 ? nsLot / nsNoSplit : 0;
      }

      // For Stock format, BCode/SCode/NBCode/NSCode are all the same as Broker (first column)
      // For Broker format, BCode/SCode/NBCode/NSCode are all the same as Emiten (first column)
      bCode = emiten;
      sCode = emiten;
      nbCode = emiten;
      nsCode = emiten;

      bVal = buyerValue;
      bAvg = buyerAvg;
      bFreq = buyerFreq;
      bOrdNum = buyerOrdNum;
      sVal = sellerValue;
      sAvg = sellerAvg;
      sFreq = sellerFreq;
      sOrdNum = sellerOrdNum;
      nbVal = netBuyValue;
      nbAvg = netBuyAvg;
      nbFreq = netBuyFreq;
      nbOrdNum = netBuyOrdNum;
      nsVal = netSellValue;
      nsAvg = netSellAvg;
      nsFreq = netSellFreq;
      nsOrdNum = netSellOrdNum;
    } else {
      // Original format: BCode;BLot;BVal;BAvg;BFreq;Lot/F;BOrdNum;Lot/ON;SCode;SLot;SVal;SAvg;SFreq;Lot/F;SOrdNum;Lot/ON;NBCode;NBLot;NBVal;NBAvg;NBFreq;NLot/F;NBOrdNum;NLot/ON;NSCode;NSLot;NSVal;NSAvg;NSFreq;NLot/F;NSOrdNum;NLot/ON
      if (values.length < 32) {
        continue;
      }

      // VALUE Table: Buyer columns (0-7)
      bCode = values[0] || '';
      bLot = parseFloat(values[1] || '0') || 0;
      bVal = parseFloat(values[2] || '0') || 0;
      bAvg = parseFloat(values[3] || '0') || 0;
      bFreq = parseFloat(values[4] || '0') || 0;
      bLotPerFreq = parseFloat(values[5] || '0') || 0;
      bOrdNum = parseFloat(values[6] || '0') || 0;
      bLotPerOrdNum = parseFloat(values[7] || '0') || 0;

      // VALUE Table: Seller columns (8-15)
      sCode = values[8] || '';
      sLot = parseFloat(values[9] || '0') || 0;
      sVal = parseFloat(values[10] || '0') || 0;
      sAvg = parseFloat(values[11] || '0') || 0;
      sFreq = parseFloat(values[12] || '0') || 0;
      sLotPerFreq = parseFloat(values[13] || '0') || 0;
      sOrdNum = parseFloat(values[14] || '0') || 0;
      sLotPerOrdNum = parseFloat(values[15] || '0') || 0;

      newBuyerOrdNum = bOrdNum;
      newSellerOrdNum = sOrdNum;

      // NET Table: Net Buy columns (16-23)
      nbCode = values[16] || '';
      nbLot = parseFloat(values[17] || '0') || 0;
      nbVal = parseFloat(values[18] || '0') || 0;
      nbAvg = parseFloat(values[19] || '0') || 0;
      nbFreq = parseFloat(values[20] || '0') || 0;
      nbLotPerFreq = parseFloat(values[21] || '0') || 0;
      nbOrdNum = parseFloat(values[22] || '0') || 0;
      nbLotPerOrdNum = parseFloat(values[23] || '0') || 0;

      // NET Table: Net Sell columns (24-31)
      nsCode = values[24] || '';
      nsLot = parseFloat(values[25] || '0') || 0;
      nsVal = parseFloat(values[26] || '0') || 0;
      nsAvg = parseFloat(values[27] || '0') || 0;
      nsFreq = parseFloat(values[28] || '0') || 0;
      nsLotPerFreq = parseFloat(values[29] || '0') || 0;
      nsOrdNum = parseFloat(values[30] || '0') || 0;
      nsLotPerOrdNum = parseFloat(values[31] || '0') || 0;

      // Legacy format has no NoSplit columns
      bNoSplit = 0; sNoSplit = 0; bLotPerNoSplit = 0; sLotPerNoSplit = 0;
      nbNoSplit = 0; nsNoSplit = 0; nbLotPerNoSplit = 0; nsLotPerNoSplit = 0;

      emiten = bCode || sCode || nbCode || nsCode;
    }

    // Calculate volumes and totals
    let finalBuyerVol: number;
    let finalSellerVol: number;
    let finalNetBuyVol: number;
    let finalNetBuyValue: number;
    let finalNetSellVol: number = 0;
    let finalNetSellValue: number = 0;

    if (isAzureFormat && isStockFormat) {
      // Stock format: volumes are already in CSV, use them directly
      finalBuyerVol = buyerVol;
      finalSellerVol = sellerVol;
      finalNetBuyVol = netBuyVol;
      finalNetBuyValue = netBuyValue;
      finalNetSellVol = netSellVol;
      finalNetSellValue = netSellValue;
    } else if (isAzureFormat) {
      // Broker format (Azure): NetBuy*/NetSell* columns are independent (e.g. for
      // ALL/{SECTOR}_ALL aggregates both sides can be nonzero at once), so read
      // them directly instead of re-deriving from nbVal - nsVal, which assumes
      // mutual exclusivity and collapses to 0 when both sides are equal.
      finalBuyerVol = bLot * 100;
      finalSellerVol = sLot * 100;
      finalNetBuyVol = netBuyVol;
      finalNetBuyValue = netBuyValue;
      finalNetSellVol = netSellVol;
      finalNetSellValue = netSellValue;
    } else {
      // Legacy semicolon-delimited format: NetBuy/NetSell are mutually exclusive,
      // recover the signed net from nbVal - nsVal.
      finalBuyerVol = bLot * 100;
      finalSellerVol = sLot * 100;

      const netBuyLot = nbLot - nsLot;
      finalNetBuyVol = netBuyLot * 100;
      finalNetBuyValue = nbVal - nsVal;
    }

    const totalVolume = finalBuyerVol + finalSellerVol;
    const totalValue = bVal + sVal;
    const avgPrice = totalVolume > 0 ? totalValue / totalVolume : 0;
    const transactionCount = Math.max(bFreq || 0, sFreq || 0);

    if (!emiten || emiten === '') {
      continue;
    }

    transactionData.push({
      Emiten: emiten,
      BuyerVol: finalBuyerVol,
      BuyerValue: bVal,
      SellerVol: finalSellerVol,
      SellerValue: sVal,
      BuyerAvg: bAvg || (finalBuyerVol > 0 ? bVal / finalBuyerVol : 0),
      SellerAvg: sAvg || (finalSellerVol > 0 ? sVal / finalSellerVol : 0),
      BCode: bCode,
      BLot: bLot,
      BFreq: bFreq,
      BLotPerFreq: bLotPerFreq,
      BOrdNum: bOrdNum,
      NewBuyerOrdNum: newBuyerOrdNum,
      BLotPerOrdNum: bLotPerOrdNum,
      SCode: sCode,
      SLot: sLot,
      SFreq: sFreq,
      SLotPerFreq: sLotPerFreq,
      SOrdNum: sOrdNum,
      NewSellerOrdNum: newSellerOrdNum,
      SLotPerOrdNum: sLotPerOrdNum,
      NetBuyVol: finalNetBuyVol,
      NetBuyValue: finalNetBuyValue,
      NetBuyAvg: nbAvg || (finalNetBuyVol > 0 ? finalNetBuyValue / finalNetBuyVol : 0),
      NetSellVol: finalNetBuyVol < 0 ? Math.abs(finalNetBuyVol) : finalNetSellVol,
      NetSellValue: finalNetBuyValue < 0 ? Math.abs(finalNetBuyValue) : finalNetSellValue,
      NetSellAvg: nsAvg || (finalNetBuyVol < 0 && Math.abs(finalNetBuyVol) > 0 ? Math.abs(finalNetBuyValue) / Math.abs(finalNetBuyVol) : (finalNetSellVol > 0 ? finalNetSellValue / finalNetSellVol : 0)),
      NBCode: nbCode || emiten,
      NBLot: nbLot,
      NBVal: nbVal,
      NBAvg: nbAvg,
      NBFreq: nbFreq,
      NBLotPerFreq: nbLotPerFreq,
      NBOrdNum: nbOrdNum,
      NBLotPerOrdNum: nbLotPerOrdNum,
      NSCode: nsCode || emiten,
      NSLot: nsLot,
      NSVal: nsVal,
      NSAvg: nsAvg,
      NSFreq: nsFreq,
      NSLotPerFreq: nsLotPerFreq,
      NSOrdNum: nsOrdNum,
      NSLotPerOrdNum: nsLotPerOrdNum,
      BNoSplit: bNoSplit,
      BLotPerNoSplit: bLotPerNoSplit,
      SNoSplit: sNoSplit,
      SLotPerNoSplit: sLotPerNoSplit,
      NBNoSplit: nbNoSplit,
      NBLotPerNoSplit: nbLotPerNoSplit,
      NSNoSplit: nsNoSplit,
      NSLotPerNoSplit: nsLotPerNoSplit,
      TotalVolume: totalVolume,
      AvgPrice: avgPrice,
      TransactionCount: transactionCount,
      TotalValue: totalValue,
    });
  }

  return transactionData;
}

const CSV_CACHE_NAME = 'broker-transaction-csv-cache';
const ZIP_FETCH_ATTEMPTS = 2;

/**
 * Fetch a legacy per-file broker_transaction CSV blob from Azure (via SAS URL),
 * using the browser Cache API for client-side caching. Returns the CSV text, or
 * null if the blob does not exist (HTTP 404).
 */
async function fetchLegacyCsv(legacyPath: string, blobUrl: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const cache = await caches.open(CSV_CACHE_NAME);
    const cached = await cache.match(blobCacheKey(legacyPath));
    if (cached) {
      return cached.text();
    }

    const response = await fetch(blobUrl, signal ? { signal } : undefined);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errText.substring(0, 200)}`);
    }

    const text = await response.text();
    await cache.put(blobCacheKey(legacyPath), new Response(text, { headers: { 'Content-Type': 'text/csv' } }));
    return text;
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
    if (err.message?.startsWith('HTTP ')) throw err;
    // Cache API unavailable — fallback to direct fetch
    const response = await fetch(blobUrl, signal ? { signal } : undefined);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${legacyPath} not found`);
    return response.text();
  }
}

/**
 * Fetch a broker_transaction CSV, preferring the per-entity ZIP bundle
 * (broker_transaction_bundle/{date}/{broker|stock}/{code}.zip) via range reads.
 * If the bundle entry can't be read after a couple of attempts, falls back to the
 * legacy per-file CSV (for dates that predate bundling). Returns null if neither
 * source has the data.
 */
export async function fetchBrokerTransactionCsv(
  paths: BrokerTransactionBlobPaths,
  containerUrl: string,
  sasToken: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const isDev = (import.meta as any).env?.DEV || false;
  const buildUrl = (path: string) => isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;

  const zipUrl = buildUrl(paths.bundlePath);
  for (let attempt = 1; attempt <= ZIP_FETCH_ATTEMPTS; attempt++) {
    try {
      const data = await fetchZipEntry(zipUrl, paths.entryName, signal);
      if (data !== null) return new TextDecoder().decode(data);
      // null TIDAK selalu berarti file tidak ada: body zip yang tidak utuh (EOCD tidak
      // ketemu, mis. lewat dev proxy di bawah beban) juga null — jadi tetap dicoba ulang.
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err;
    }
  }

  return fetchLegacyCsv(paths.legacyPath, buildUrl(paths.legacyPath), signal);
}
